/**
 * Felanmälan från ärende for guests (#1650): photos are uploaded first under
 * `issues/{issueKey}/photos/` through signed URLs, then the issue is created
 * by `guestApi.reportIssue`. The key is a Firebase auto-ID made locally (no
 * database access is needed for that). Photos of a report that fails or is
 * abandoned stay in Storage, as with the public report.
 */
import { useCallback, useState } from 'react';
import { getDatabase, push, ref } from 'firebase/database';
import { guestApi } from '../../../api/guestApi';
import { GuestReportIssuePayload } from '../../../types/guestAccess';
import { getImageDimensions } from './useGuestProject';

export interface GuestIssueReport {
    description: string;
    priority: GuestReportIssuePayload['priority'];
    componentKey?: string;
    files: File[];
}

export function useGuestReportIssue(token: string, workOrderKey: string) {
    const [submitting, setSubmitting] = useState(false);

    const report = useCallback(async ({ description, priority, componentKey, files }: GuestIssueReport): Promise<string> => {
        setSubmitting(true);
        try {
            const issueKey = push(ref(getDatabase(), 'issues/active')).key!;
            const photos: NonNullable<GuestReportIssuePayload['photos']> = [];
            for (const file of files) {
                const { uploadUrl, key, storagePath } = await guestApi.getIssueUploadUrl(token, workOrderKey, issueKey, file.name, file.type);
                const response = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
                if (!response.ok) throw new Error('Kunde inte ladda upp bilden');
                const { width, height } = await getImageDimensions(file);
                photos.push({ key, storagePath, width, height, fileSize: file.size });
            }
            return await guestApi.reportIssue(token, workOrderKey, {
                issueKey,
                description: description.trim(),
                priority,
                ...(componentKey ? { componentKey } : {}),
                ...(photos.length > 0 ? { photos } : {}),
            });
        } finally {
            setSubmitting(false);
        }
    }, [token, workOrderKey]);

    return { report, submitting };
}
